import React, { useRef, useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaSearch, FaCalendarAlt } from "react-icons/fa";
import {
  getCofRequests,
  deleteCofRequest,
  fetchFishermenDetails,
} from "../../../../api/cof";
import { getInspectors } from "../../../../api/BoatInspector/InspectorApi";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import { motion } from "framer-motion";
import { AuthContext } from "../../../ExtraPages/AuthProvider";
import COFRequest from "./cofrequest";

const CofRequests = () => {
  const navigate = useNavigate();
  const [allCofRequests, setAllCofRequests] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFishermenModalOpen, setIsFishermenModalOpen] = useState(false);
  const [selectedFishermen, setSelectedFishermen] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [tableKey, setTableKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const childRef = useRef();
  const { user } = useContext(AuthContext);
  const [userId, setUserId] = useState(null);
  const [cofSubmitted, setCofSubmitted] = useState(false);

  const statusClassMap = {
    Rejected: "status-button-danger",
    Pending: "status-button-warning",
    Accepted: "status-button-success",
  };

  useEffect(() => {
    if (!user) return;
    setUserId(user.uid);
  }, [user]);

  useEffect(() => {
    if (!userId) return;

    const fetchCofRequests = async () => {
      try {
        const response = await getCofRequests(userId);
        console.log("Fetched COF requests:", response.data);
        setAllCofRequests([...response.data]);
      } catch (err) {
        console.error("Error fetching CofRequests:", err);
        setAllCofRequests([]);
      }
    };

    const fetchInspectors = async () => {
      try {
        const response = await getInspectors();
        setInspectors(response.data.data);
      } catch (err) {
        console.error("Error fetching inspectors:", err);
      }
    };

    fetchCofRequests();
    fetchInspectors();
  }, [userId]);

  const filteredData = React.useMemo(() => {
    return allCofRequests.filter((item) => {
      const matchesText =
        item.status.toLowerCase().includes(filterText.toLowerCase()) ||
        item.vesselNumber.toLowerCase().includes(filterText.toLowerCase()) ||
        item.officerName?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.harbour.toLowerCase().includes(filterText.toLowerCase());

      const itemDate = new Date(item.requestDate);
      const startDate = dateRange.start ? new Date(dateRange.start) : null;
      const endDate = dateRange.end ? new Date(dateRange.end) : null;

      const matchesDate =
        (!startDate || itemDate >= startDate) &&
        (!endDate || itemDate <= endDate);

      return matchesText && matchesDate;
    });
  }, [allCofRequests, filterText, dateRange]);

  const triggerChildSubmit = async () => {
    if (childRef.current) {
      setIsSubmitting(true);
      setCofSubmitted(true);
      try {
        await childRef.current.handleSubmit1();
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const response = await getCofRequests(userId);
        console.log("Post-submission COF requests:", response.data);
        setAllCofRequests([...response.data]);
        setTableKey((prev) => prev + 1);
        setIsModalOpen(false);
        Swal.fire({
          title: "Success!",
          text: "COF Request added successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
      } catch (err) {
        console.error("Error submitting COF request:", err);
        Swal.fire({
          title: "Error!",
          text:
            "Failed to add COF Request: " + (err.message || "Unknown error"),
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const deleteHandler = async (event) => {
    const id = event.target.id;

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteCofRequest(id);
          const response = await getCofRequests(userId);
          console.log("Post-deletion COF requests:", response.data);
          setAllCofRequests([...response.data]);
          setTableKey((prev) => prev + 1);
          Swal.fire({
            title: "Deleted!",
            text: "COF Request has been deleted.",
            icon: "success",
            confirmButtonText: "OK",
          });
        } catch (error) {
          console.error("Error deleting COF request:", error);
          Swal.fire({
            title: "Error!",
            text: "Failed to delete COF Request.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const showFishermen = async (fishermenIds) => {
    try {
      // Filter out null, empty, or invalid IDs
      const validIds = fishermenIds.filter((id) => id && id.trim() !== "");
      if (validIds.length === 0) return;

      // Assuming an API endpoint to fetch fishermen details by IDs
      const response = await fetchFishermenDetails(validIds);
      console.log("Fetched fishermen details:", response);
      setSelectedFishermen(response || []);
      setIsFishermenModalOpen(true);
    } catch (err) {
      console.error("Error fetching fishermen details:", err);
      Swal.fire({
        title: "Error!",
        text: "Failed to fetch fishermen details.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const toggleModal = () => {
    if (!isSubmitting) {
      setIsModalOpen(!isModalOpen);
      setCofSubmitted(false);
    }
  };

  const toggleFishermenModal = () => {
    setIsFishermenModalOpen(!isFishermenModalOpen);
  };

  const clearFilters = () => {
    setFilterText("");
    setDateRange({ start: "", end: "" });
  };

  const columns = [
    {
      id: 1,
      name: 'Request Date',
      selector: (row) => row.requestDate,
      sortable: true,
      sortFunction: (rowA, rowB) => {
        const a = new Date(rowA.requestDate);
        const b = new Date(rowB.requestDate);
        return a - b;
      },
      minWidth: '140px',
      maxWidth: '160px',
    },
    {
      name: 'Time Slot (24h)',
      selector: (row) => row.timeSlot,
      sortable: true,
      minWidth: '140px',
      maxWidth: '160px',
    },
    {
      name: 'Vessel Number',
      selector: (row) => row.vesselNumber,
      minWidth: '140px',
      maxWidth: '180px',
    },
    {
      name: 'Harbour',
      selector: (row) => row.harbour,
      sortable: true,
      minWidth: '150px',
      grow: 1,
    },
    {
      name: 'Assigned Officer',
      selector: (row) => row.officerName || 'Not Assigned',
      sortable: true,
      minWidth: '180px',
      grow: 2,
    },
    {
      name: 'Status',
      selector: (row) => (
        <span className={`badge ${statusClassMap[row.status]}`}>
          {row.status}
        </span>
      ),
      sortable: true,
      center: true,
      minWidth: '110px',
      maxWidth: '130px',
    },
    {
      name: 'Fishermen',
      selector: (row) => {
        const hasValidFishermen =
          Array.isArray(row.fishermen) &&
          row.fishermen.some((id) => id && id.trim() !== '');
        return hasValidFishermen ? (
          <Button
            size="sm"
            color="info"
            onClick={() => showFishermen(row.fishermen)}
            disabled={isSubmitting}
            className="px-3"
          >
            View ({row.fishermen.length})
          </Button>
        ) : (
          <span className="text-muted">None</span>
        );
      },
      minWidth: '130px',
      maxWidth: '150px',
      center: true,
    },
    {
      name: 'Actions',
      selector: (row) =>
        row.status === 'Pending' ? (
          <Button
            size="sm"
            color="danger"
            id={row._id}
            onClick={deleteHandler}
            disabled={isSubmitting}
            className="px-3"
          >
            Delete
          </Button>
        ) : (
          <span className="text-muted">No Actions</span>
        ),
      sortable: true,
      center: true,
      minWidth: '120px',
      maxWidth: '140px',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <Container fluid>
        <Row>
          <Col md="12">
            <Card className="shadow-sm border-0">
              <CardBody className="p-4">
                {/* Header Section */}
                <Row className="mb-4 align-items-center">
                  <Col md="6">
                    <h4 className="mb-0 text-primary fw-bold">
                      My COF Requests
                    </h4>
                    <small className="text-muted">
                      Manage your Certificate of Fitness requests
                    </small>
                  </Col>
                  <Col md="6">
                    <div className="text-end">
                      <Button
                        color="primary"
                        onClick={toggleModal}
                        disabled={isSubmitting}
                        className="btn-lg px-4 py-2 rounded-pill shadow-sm"
                        style={{
                          background:
                            'linear-gradient(135deg, #003B8F, #0056d6)',
                          border: 'none',
                        }}
                      >
                        <FaPlus className="me-2" />
                        New COF Request
                      </Button>
                    </div>
                  </Col>
                </Row>

                {/* Filters Section */}
                <Card className="bg-light border-0 mb-4">
                  <CardBody className="p-3">
                    <Row className="align-items-end g-3">
                      <Col md="4">
                        <div className="form-group">
                          <label className="form-label fw-semibold text-dark mb-2">
                            Search
                          </label>
                          <InputGroup>
                            <InputGroupText className="bg-white border-end-0">
                              <FaSearch className="text-muted" />
                            </InputGroupText>
                            <Input
                              type="text"
                              placeholder="Search by status, vessel, officer, harbour..."
                              value={filterText}
                              onChange={(e) => setFilterText(e.target.value)}
                              className="border-start-0 ps-0"
                              disabled={isSubmitting}
                              style={{ boxShadow: 'none' }}
                            />
                          </InputGroup>
                        </div>
                      </Col>
                      <Col md="3">
                        <div className="form-group">
                          <label className="form-label fw-semibold text-dark mb-2">
                            <FaCalendarAlt className="me-1" />
                            Start Date
                          </label>
                          <Input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) =>
                              setDateRange({
                                ...dateRange,
                                start: e.target.value,
                              })
                            }
                            disabled={isSubmitting}
                            className="form-control"
                          />
                        </div>
                      </Col>
                      <Col md="3">
                        <div className="form-group">
                          <label className="form-label fw-semibold text-dark mb-2">
                            <FaCalendarAlt className="me-1" />
                            End Date
                          </label>
                          <Input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) =>
                              setDateRange({
                                ...dateRange,
                                end: e.target.value,
                              })
                            }
                            disabled={isSubmitting}
                            className="form-control"
                          />
                        </div>
                      </Col>
                      <Col md="2">
                        <div className="form-group">
                          <Button
                            color="secondary"
                            outline
                            onClick={clearFilters}
                            disabled={isSubmitting}
                            className="w-100"
                          >
                            Clear Filters
                          </Button>
                        </div>
                      </Col>
                    </Row>

                    {/* Filter Summary */}
                    <Row className="mt-3">
                      <Col>
                        <div className="d-flex align-items-center gap-3 text-sm">
                          <span className="text-muted">
                            <strong>Total Records:</strong>{' '}
                            {allCofRequests.length}
                          </span>
                          <span className="text-muted">
                            <strong>Filtered Results:</strong>{' '}
                            {filteredData.length}
                          </span>
                          {(filterText || dateRange.start || dateRange.end) && (
                            <span className="badge bg-info">
                              Filters Active
                            </span>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Data Table */}
                <div className="table-responsive">
                  <DataTable
                    key={tableKey}
                    columns={columns}
                    data={filteredData}
                    defaultSortFieldId={1}
                    defaultSortAsc={false}
                    pagination
                    paginationResetDefaultPage={tableKey}
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[5, 10, 15, 20, 50]}
                    highlightOnHover
                    striped
                    responsive
                    dense
                    fixedHeader
                    fixedHeaderScrollHeight="600px"
                    noDataComponent={
                      <div className="text-center py-5">
                        <div className="text-muted">
                          <h5>No COF requests found</h5>
                          <p>
                            {filterText || dateRange.start || dateRange.end
                              ? 'Try adjusting your filters'
                              : "Click 'New COF Request' to create your first request"}
                          </p>
                        </div>
                      </div>
                    }
                    customStyles={{
                      table: {
                        style: {
                          width: '100%',
                          minWidth: '100%',
                        },
                      },
                      headRow: {
                        style: {
                          backgroundColor: '#f8f9fa',
                          borderBottom: '2px solid #dee2e6',
                          minHeight: '48px',
                        },
                      },
                      headCells: {
                        style: {
                          fontWeight: '600',
                          fontSize: '14px',
                          color: '#495057',
                          padding: '12px 8px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        },
                      },
                      rows: {
                        style: {
                          minHeight: '56px',
                          '&:hover': {
                            backgroundColor: '#f8f9fa',
                            cursor: 'pointer',
                          },
                        },
                      },
                      cells: {
                        style: {
                          fontSize: '14px',
                          padding: '12px 8px',
                          wordWrap: 'break-word',
                          wordBreak: 'break-word',
                        },
                      },
                      pagination: {
                        style: {
                          border: 'none',
                          fontSize: '14px',
                          color: '#6c757d',
                          backgroundColor: '#f8f9fa',
                          borderTop: '1px solid #dee2e6',
                          minHeight: '56px',
                        },
                      },
                    }}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* COF Request Modal */}
        <Modal
          isOpen={isModalOpen}
          toggle={toggleModal}
          size="lg"
          backdrop="static"
        >
          <ModalHeader
            toggle={toggleModal}
            className="bg-primary text-white"
            style={{
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
            }}
          >
            <div className="d-flex align-items-center">
              <FaPlus className="me-2" />
              Request Certificate of Fitness
            </div>
          </ModalHeader>
          <ModalBody className="p-4">
            <COFRequest
              ref={childRef}
              cofSubmitted={cofSubmitted}
              toggleModal={toggleModal}
              inspectors={inspectors}
            />
          </ModalBody>
          <ModalFooter className="bg-light">
            <Button
              color="primary"
              onClick={triggerChildSubmit}
              disabled={isSubmitting}
              className="px-4"
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
            <Button
              color="secondary"
              onClick={toggleModal}
              disabled={isSubmitting}
              className="px-4"
            >
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        {/* Fishermen Modal */}
        <Modal
          isOpen={isFishermenModalOpen}
          toggle={toggleFishermenModal}
          size="md"
        >
          <ModalHeader
            toggle={toggleFishermenModal}
            className="bg-info text-white"
          >
            Fishermen Details
          </ModalHeader>
          <ModalBody className="p-4">
            {selectedFishermen.length > 0 ? (
              <div className="list-group list-group-flush">
                {selectedFishermen.map((fisherman, index) => (
                  <div
                    key={index}
                    className="list-group-item border-0 px-0 py-2"
                  >
                    <div className="d-flex align-items-center">
                      <div className="avatar-sm bg-light rounded-circle d-flex align-items-center justify-content-center me-3">
                        <span className="text-muted fw-bold">
                          {(fisherman.data.firstName || 'U')
                            .charAt(0)
                            .toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h6 className="mb-0">
                          {fisherman.data.firstName}{' '}
                          {fisherman.data.lastName || 'Unk}nown Fisherman'}
                        </h6>
                        {fisherman.id && (
                          <small className="text-muted">
                            ID: {fisherman.data.userId}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="text-muted">
                  <h6>No fishermen details available</h6>
                  <p className="mb-0">
                    No fishermen are associated with this request.
                  </p>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={toggleFishermenModal}
              className="px-4"
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </Container>

      <style jsx>{`
        .avatar-sm {
          width: 40px;
          height: 40px;
        }

        .status-button-danger,
        .badge.status-button-danger {
          background-color: #dc3545 !important;
          color: white !important;
        }

        .status-button-warning,
        .badge.status-button-warning {
          background-color: #ffc107 !important;
          color: #212529 !important;
        }

        .status-button-success,
        .badge.status-button-success {
          background-color: #28a745 !important;
          color: white !important;
        }

        .text-sm {
          font-size: 0.875rem;
        }

        .btn-lg {
          font-size: 1rem;
          font-weight: 500;
        }

        .form-label {
          margin-bottom: 0.5rem;
        }

        .table-responsive {
          border-radius: 0.5rem;
          overflow: hidden;
          box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
        }

        .shadow-sm {
          box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
        }

        /* Full width table container */
        .table-responsive {
          width: 100%;
          margin: 0;
        }

        /* Ensure table uses full available space */
        div[role='table'] {
          width: 100% !important;
          min-width: 100% !important;
        }

        /* Better responsive behavior for table */
        @media (max-width: 768px) {
          .table-responsive {
            font-size: 13px;
          }

          .btn-sm {
            font-size: 12px;
            padding: 4px 8px;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default CofRequests;
